import type { ClientDerivativeOptions } from '../calcClientDerivatives';

// A timezone-neutral calendar date, `YYYY-MM-DD`.
//
// The calc reads its clock through LOCAL getters (`getFullYear`, `getMonth`,
// `getDate`), so an instant is not a reproducible input: `2026-06-15T00:00:00Z`
// is June 15 in UTC but June 14 in America/Chicago, which shifts client ages by
// a year and `calculateMonthsBetweenYears` by a month. A calendar date has no
// timezone to disagree about — parsed to local midnight, its local getters
// return exactly the pinned year/month/day wherever the code runs.
export type CalendarDate = string;

const CALENDAR_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

// The calendar date it currently is *where this code is running*. Local by
// design: it is the date the advisor sees on screen.
export function toCalendarDate(date: Date): CalendarDate {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// Local midnight of the given calendar date, so every local getter downstream
// reports the pinned year/month/day in any timezone.
export function parseCalendarDate(value: CalendarDate): Date {
  const match = CALENDAR_DATE_PATTERN.exec(value);
  if (!match) {
    throw new Error(
      `asOfDate must be a YYYY-MM-DD calendar date, got ${JSON.stringify(value)}. ` +
        'A Date or ISO timestamp is ambiguous across timezones — convert it with toCalendarDate().',
    );
  }

  const [, year, month, day] = match;
  const parsed = new Date(Number(year), Number(month) - 1, Number(day));
  if (
    parsed.getFullYear() !== Number(year) ||
    parsed.getMonth() !== Number(month) - 1 ||
    parsed.getDate() !== Number(day)
  ) {
    throw new Error(`asOfDate must be a valid calendar date, got ${JSON.stringify(value)}.`);
  }
  return parsed;
}

// API Gateway/Lambda may coerce a YYYY-MM-DD JSON value to a Date at UTC
// midnight. Recover the original calendar components without applying the
// server's local timezone, then validate strings through the same strict path.
export function normalizeCalendarDateInput(value: CalendarDate | Date): CalendarDate {
  const calendarDate = value instanceof Date ? value.toISOString().slice(0, 10) : value;
  parseCalendarDate(calendarDate);
  return calendarDate;
}

// Single resolution point for the pass's "today", mirroring
// `resolveUseClaimProjectionEngine`: an explicit option wins (callers that pin a
// date without mutating the client), then the date the derivative pipeline
// stamped onto the client, then the real current date.
//
// `calcClientDerivativesImpl` stamps `calcAsOfDate` before any other calc runs,
// so every downstream site inside a pass sees the same clock — one calc can't
// straddle a month or year boundary, and pinning `asOfDate` makes the whole pass
// reproducible. Outside a pass the client's value may be stale; prefer passing
// the date explicitly there.
export function resolveAsOfCalendarDate(
  client: Client,
  options: ClientDerivativeOptions = {},
): CalendarDate {
  return options.asOfDate ?? client.calcAsOfDate ?? toCalendarDate(new Date());
}

// The same clock as a Date, at local midnight — for helpers that need month or
// day precision (`calculateAge`, `calculateMonthsBetweenYears`).
export function resolveAsOfDate(client: Client, options: ClientDerivativeOptions = {}): Date {
  return parseCalendarDate(resolveAsOfCalendarDate(client, options));
}

// Year-only convenience for the many sites that just need "the current year".
// Prefers `costInflationAnchorYear` when present so this can never disagree with
// the anchor the inflation chain already uses.
export function resolveAsOfYear(client: Client, options: ClientDerivativeOptions = {}): number {
  if (options.asOfDate) {
    return parseCalendarDate(options.asOfDate).getFullYear();
  }
  return client.costInflationAnchorYear ?? resolveAsOfDate(client, options).getFullYear();
}
